import { useForm } from '@inertiajs/react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toastFlash } from '@/lib/flash-toast';
import type { TrackAdmin } from '@/types';

interface EditTrackDialogProps {
  eventSlug: string;
  track: TrackAdmin;
  onClose: () => void;
}

// Mounted only while a track is being edited and keyed on its id, which is what the
// `form.reset(...)` inside the Next dialog's effect achieved.
export function EditTrackDialog({
  eventSlug,
  track,
  onClose,
}: EditTrackDialogProps) {
  const form = useForm({
    name: track.name,
    description: track.description || '',
  });
  const { data, setData, processing } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.patch(`/admin/${eventSlug}/tracks/${track.id}`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'Track updated');
        onClose();
      },
      onError: () =>
        toast({ type: 'error', description: 'Failed to update track' }),
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Track</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-track-name">Track Name</Label>
            <Input
              id="edit-track-name"
              placeholder="e.g., AI & Machine Learning"
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
            />
            {errors.name && (
              <p className="text-destructive text-sm">{errors.name}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-track-description">
              Description (optional)
            </Label>
            <Textarea
              id="edit-track-description"
              placeholder="Brief description of this track"
              value={data.description}
              onChange={(e) => setData('description', e.target.value)}
            />
            {errors.description && (
              <p className="text-destructive text-sm">{errors.description}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
